import { useMutation, useQuery } from "@tanstack/react-query";

import type {
	GetAutoflowByIdResponseJson,
	PatchAutoflowRequest,
	PostAutoflowPreviewRequest,
	PostAutoflowPreviewResponseJson,
	PostAutoflowRequest,
} from "@performyard/service-contracts";

import type { UserQuery } from "../../../components/UserQueryBuilder/types";

import { getAutoflowByResourceId, getAutoflowPreview, patchAutoflow, postAutoflow } from "../../services";
import type { PYUseMutationOptions, PYUseQueryOptions } from "../StandardQueryArguments";

export const useAutoflowByResourceId = (
	resourceId: string,
	options?: PYUseQueryOptions<GetAutoflowByIdResponseJson>
) => {
	return useQuery<GetAutoflowByIdResponseJson>({
		queryKey: ["autoflow-resource", resourceId],
		queryFn: async () => await getAutoflowByResourceId(resourceId),
		meta: {
			disableDefaultErrorHandling: true,
			errorMessage: "There was an error getting the autoflow by resource ID.",
		},
		enabled: !!resourceId,
		...options,
	});
};

type QueryType = Extract<PostAutoflowPreviewRequest, { tasks: { config: { query: UserQuery } } }>;

export const useAutoflowPreview = (query: UserQuery, options?: PYUseQueryOptions<PostAutoflowPreviewResponseJson>) => {
	const autoflowPreviewRequest: PostAutoflowPreviewRequest = {
		tasks: [
			{
				taskId: "query-user-01",
				taskType: "query:user",
				retries: 1,
				config: {
					query: query as QueryType,
				},
			},
		],
	};
	return useQuery<PostAutoflowPreviewResponseJson>({
		queryKey: ["autoflow-preview", autoflowPreviewRequest],
		queryFn: async () => await getAutoflowPreview(autoflowPreviewRequest),
		meta: {
			errorMessage: "There was an error previewing the autoflow.",
		},
		enabled: !!query,
		...options,
	});
};

export const useCreateAutoflow = (options?: PYUseMutationOptions) => {
	return useMutation({
		...options,
		mutationFn: async (props: { autoflow: PostAutoflowRequest }) => await postAutoflow(props.autoflow),
	});
};

export const usePatchAutoflow = (options?: PYUseMutationOptions) => {
	return useMutation({
		...options,
		mutationFn: async (props: { id: string; data: PatchAutoflowRequest }) =>
			await patchAutoflow(props.id, props.data),
		onSuccess: (response, variables, context) => {
			options?.onSuccess?.(response, variables, context);
		},
		onError: (error, variables, context) => {
			options?.onError?.(error, variables, context);
		},
	});
};
